import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  ContentPaste as PasteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brand, primaryButtonSx } from '../theme';
import AuditSummary from '../components/AuditReport/AuditSummary';
import AreaDetailCard from '../components/AuditReport/AreaDetailCard';
import customsAuditService, {
  AuditReport,
  AuditRequest,
  AuditHistoryResponse,
} from '../services/customsAuditService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SAMPLE_DECLARATION = JSON.stringify(
  {
    kopf: {
      anmeldungsart: 'CO',
      ursprungsland: 'CN',
      bestimmungsland: 'DE',
      waehrung: 'EUR',
      rohmasse: 500.0,
      empfanger: { name: 'Acme GmbH', strasse: 'Musterstr. 1', plz: '10115', ort: 'Berlin', land: 'DE' },
      ausfuhrer: { name: 'Shanghai Trading Co.', land: 'CN' },
    },
    positionen: [
      {
        warenbezeichnung: 'Electric motor, AC, output 5kW, for industrial use',
        warennummerKombinierteNomenklatur: '85015200',
        statistischerWert: 2500,
        eigenmasse: 150.0,
        rohmasse: 170.0,
        ursprungsland: 'CN',
        anmeldungsmenge: 10,
      },
      {
        warenbezeichnung: 'Steel mounting brackets',
        warennummerKombinierteNomenklatur: '73089090',
        statistischerWert: 800,
        eigenmasse: 50.0,
        rohmasse: 55.0,
        ursprungsland: 'CN',
        anmeldungsmenge: 100,
      },
    ],
  },
  null,
  2
);

const CustomsAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [declarationJson, setDeclarationJson] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [report, setReport] = useState<AuditReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // History state
  const [history, setHistory] = useState<AuditHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await customsAuditService.getHistory(
        historyPage * historyRowsPerPage,
        historyRowsPerPage
      );
      setHistory(data);
    } catch (err) {
      console.error('Failed to load audit history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyRowsPerPage]);

  useEffect(() => {
    if (tabValue === 1) {
      loadHistory();
    }
  }, [tabValue, loadHistory]);

  const handleRunAudit = async () => {
    setError(null);
    setJsonError(null);
    setReport(null);

    // Validate JSON
    let parsed: any;
    try {
      parsed = JSON.parse(declarationJson);
    } catch {
      setJsonError('Invalid JSON. Please paste valid declaration data.');
      return;
    }

    if (!parsed.kopf && !parsed.positionen) {
      setJsonError('Declaration must contain "kopf" (header) or "positionen" (items).');
      return;
    }

    const request: AuditRequest = {
      declaration_data: parsed,
      documents: [],
      supporting_files: [],
    };

    setIsRunning(true);
    try {
      const result = await customsAuditService.runFullAudit(request);
      setReport(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Audit failed');
    } finally {
      setIsRunning(false);
    }
  };

  const handleLoadSample = () => {
    setDeclarationJson(SAMPLE_DECLARATION);
    setJsonError(null);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'compliant': return 'success';
      case 'requires_review': return 'warning';
      case 'non_compliant': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: brand.textPrimary }}>
            Customs Audit
          </Typography>
          <Typography variant="body1" color="text.secondary">
            7-area unified audit: description, tariff, price, origin, export control, physical, and tampering detection.
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{
              borderBottom: `1px solid ${brand.borderSubtle}`,
              px: 2,
              '& .MuiTabs-indicator': { backgroundColor: brand.orange },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: `${brand.orange} !important` },
            }}
          >
            <Tab icon={<AssessmentIcon />} label="Run Audit" iconPosition="start" />
            <Tab icon={<HistoryIcon />} label="History" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab 0: Run Audit */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Input */}
            <Grid item xs={12} md={report ? 5 : 12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Declaration Data
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<PasteIcon />}
                    onClick={handleLoadSample}
                  >
                    Load Sample
                  </Button>
                </Box>
                <TextField
                  multiline
                  fullWidth
                  minRows={12}
                  maxRows={24}
                  value={declarationJson}
                  onChange={(e) => {
                    setDeclarationJson(e.target.value);
                    setJsonError(null);
                  }}
                  placeholder='Paste declaration JSON here (with "kopf" and "positionen")...'
                  error={!!jsonError}
                  helperText={jsonError}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                    },
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                    onClick={handleRunAudit}
                    disabled={isRunning || !declarationJson.trim()}
                    sx={{ ...primaryButtonSx, px: 4 }}
                  >
                    {isRunning ? 'Running Audit...' : 'Run Full Audit'}
                  </Button>
                </Box>
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Results */}
            {report && (
              <Grid item xs={12} md={7}>
                <AuditSummary report={report} />
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Detailed Area Results
                </Typography>
                {report.area_results.map((area) => (
                  <AreaDetailCard key={area.area} result={area} />
                ))}
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: History */}
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Audit History
            </Typography>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : history && history.results.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Risk Score</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Areas</TableCell>
                        <TableCell>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.results.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            {new Date(row.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Typography
                              fontWeight={600}
                              color={
                                row.overall_risk_score >= 60
                                  ? 'error.main'
                                  : row.overall_risk_score >= 30
                                  ? 'warning.main'
                                  : 'success.main'
                              }
                            >
                              {row.overall_risk_score}/100
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.overall_status.replace('_', ' ').toUpperCase()}
                              color={getStatusColor(row.overall_status)}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell>
                            {row.area_results?.length || 0} areas
                          </TableCell>
                          <TableCell>
                            {row.processing_time_ms}ms
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={history.total}
                  page={historyPage}
                  onPageChange={(_, p) => setHistoryPage(p)}
                  rowsPerPage={historyRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setHistoryRowsPerPage(parseInt(e.target.value, 10));
                    setHistoryPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No audit history yet. Run your first audit to see results here.
              </Typography>
            )}
          </Paper>
        </TabPanel>
      </Container>
    </Box>
  );
};

export default CustomsAuditPage;
